import React, { useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaBuilding, FaCamera, FaChartLine, FaDownload, FaEnvelope, FaFileAlt } from 'react-icons/fa';
import ReportCard from '../Components/ReportCard';

const RESEARCH_URL = 'https://www.meshum.com/research';
const SHARE_IMAGE = 'https://meshum.com/share-thumbnail.jpg';
const LOGO_URL = 'https://meshumimages.blob.core.windows.net/websiteimages/1.png';

const marketReports = [];

const methodologyItems = [
  {
    title: 'Active Listings',
    copy: 'Current dealer inventory provides visibility into asking prices and local supply.',
  },
  {
    title: 'Recently Sold Comparables',
    copy: 'Comparable vehicles help establish what similar cars have recently sold for.',
  },
  {
    title: 'Vehicle-Level Adjustments',
    copy: 'Meshum considers mileage, model year, trim, accident history, and geographic differences.',
  },
  {
    title: 'Market Estimates',
    copy: 'Comparable data is used to produce a vehicle-specific Meshum Estimate when sufficient data is available.',
  },
];

const companyFacts = [
  ['Company', 'Meshum Inc.'],
  ['Product', 'Used-car search, pricing insights, and comparable sales'],
  ['Website', 'www.meshum.com'],
  ['Location', 'Miami, Florida'],
  ['Founded by', 'Loresh Persad'],
];

const mediaResources = [
  {
    title: 'Meshum logo',
    copy: 'Approved brand logo for media use.',
    icon: FaDownload,
    preview: LOGO_URL,
  },
  {
    title: 'Product screenshots',
    copy: 'Available upon request',
    icon: FaCamera,
  },
  {
    title: 'Company overview',
    copy: 'Available upon request',
    icon: FaFileAlt,
  },
  {
    title: 'Founder information',
    copy: 'Available upon request',
    icon: FaBuilding,
  },
];

const researchTopics = [
  'South Florida used-car prices',
  'Miami vehicle inventory',
  'Used-car depreciation',
  'Luxury vehicle pricing',
  'Electric vehicle values',
  'Trucks and SUVs',
  'Best-value used vehicles',
  'Dealer asking prices versus comparable sales',
];

function Research() {
  const reportsRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Used Car Market Research & Media | Meshum',
    description: 'Explore used car market research, pricing insights, company information, and media resources from Meshum.',
    url: RESEARCH_URL,
    publisher: {
      '@type': 'Organization',
      name: 'Meshum Inc.',
      url: 'https://www.meshum.com',
      logo: LOGO_URL,
    },
  };

  return (
    <main className="bg-slate-50">
      <Helmet>
        <title>Used Car Market Research & Media | Meshum</title>
        <meta
          name="description"
          content="Explore used car market research, pricing insights, company information, and media resources from Meshum."
        />
        <link rel="canonical" href={RESEARCH_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Used Car Market Research & Media | Meshum" />
        <meta
          property="og:description"
          content="Explore used car market research, pricing insights, company information, and media resources from Meshum."
        />
        <meta property="og:url" content={RESEARCH_URL} />
        <meta property="og:image" content={SHARE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Used Car Market Research & Media | Meshum" />
        <meta
          name="twitter:description"
          content="Explore used car market research, pricing insights, company information, and media resources from Meshum."
        />
        <meta name="twitter:image" content={SHARE_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
            Research & Media
          </div>
          <h1 className="mt-5 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
            Used Car Market Research
          </h1>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Original pricing insights, market reports, and resources from Meshum—built from active vehicle listings, recently sold comparables, and real-world market data.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => scrollToSection(reportsRef)}
              className="inline-flex h-12 items-center justify-center rounded-lg bg-blue-700 px-5 text-base font-semibold text-white transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              View Market Reports
            </button>
            <button
              type="button"
              onClick={() => scrollToSection(contactRef)}
              className="inline-flex h-12 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-base font-semibold text-slate-900 transition hover:border-blue-300 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              Contact Meshum
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950">About Meshum</h2>
            <p className="mt-4 leading-7 text-slate-600">
              Meshum helps buyers understand what a used car is worth by comparing active listings with recently sold comparable vehicles. The Meshum Estimate accounts for factors such as model year, mileage, trim, accident history, and location to provide a clearer view of a vehicle's market value.
            </p>
            <p className="mt-4 leading-7 text-slate-600">
              Meshum was built to make used-car pricing more transparent and give buyers better information before they visit a dealership or negotiate a purchase.
            </p>
            <Link
              to="/about"
              className="mt-5 inline-flex text-sm font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800"
            >
              Learn more about Meshum
            </Link>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Meshum at a Glance</h2>
            <dl className="mt-5 divide-y divide-slate-100">
              {companyFacts.map(([label, value]) => (
                <div key={label} className="grid grid-cols-[120px_1fr] gap-4 py-3 text-sm">
                  <dt className="font-semibold text-slate-500">{label}</dt>
                  <dd className="text-slate-900">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section
        ref={reportsRef}
        id="reports"
        className="mx-auto max-w-6xl scroll-mt-8 px-4 py-10 sm:px-6 lg:px-8"
      >
        <div className="max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Market Reports and Data Studies</h2>
          <p className="mt-3 leading-7 text-slate-600">
            Meshum publishes original research on used-car prices, depreciation, inventory, and regional market trends.
          </p>
        </div>

        {marketReports.length > 0 ? (
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {marketReports.map((report) => (
              <ReportCard key={report.url || report.title} report={report} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-dashed border-blue-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                <FaChartLine aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-950">
                  Our first South Florida used-car market report is coming soon.
                </h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Future reports will examine pricing movements, vehicle depreciation, inventory changes, and notable deals across Miami and the surrounding market.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-semibold tracking-tight text-slate-950">How Meshum Analyzes the Market</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {methodologyItems.map((item) => (
            <article key={item.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{item.copy}</p>
            </article>
          ))}
        </div>
        <p className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
          Meshum Estimates and market reports are informational and should not be considered appraisals, guarantees, or offers to purchase or sell a vehicle.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Media Resources</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {mediaResources.map((resource) => {
            const Icon = resource.icon;
            return (
              <article key={resource.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                {resource.preview ? (
                  <div className="flex h-20 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <img src={resource.preview} alt="Meshum logo" className="max-h-10 object-contain" />
                  </div>
                ) : (
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <Icon aria-hidden="true" />
                  </div>
                )}
                <h3 className="mt-4 text-lg font-semibold text-slate-950">{resource.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{resource.copy}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Research Topics</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {researchTopics.map((topic) => (
            <span
              key={topic}
              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            >
              {topic}
            </span>
          ))}
        </div>
      </section>

      <section
        ref={contactRef}
        id="media-contact"
        className="mx-auto max-w-6xl scroll-mt-8 px-4 py-10 sm:px-6 lg:px-8"
      >
        <div className="rounded-xl bg-slate-950 p-6 text-white shadow-sm sm:p-8">
          <h2 className="text-3xl font-semibold tracking-tight">Media and Data Inquiries</h2>
          <p className="mt-3 max-w-3xl leading-7 text-slate-200">
            Journalists, researchers, automotive creators, and organizations interested in Meshum data or commentary can contact us for additional information.
          </p>
          <a
            href="mailto:contact@meshum.com"
            className="mt-6 inline-flex h-12 items-center justify-center rounded-lg bg-white px-5 text-base font-semibold text-slate-950 no-underline transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <FaEnvelope className="mr-2" aria-hidden="true" />
            Contact Meshum
          </a>
        </div>
      </section>
    </main>
  );
}

export default Research;
